import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Button,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import ResponsiveTable from '../../../components/ResponsiveTable';
import { adminHasPermission } from '../../../utils/adminPermissions';
import StudentPurchaseResourcesDialog from './StudentPurchaseResourcesDialog';

const StudentList = () => {
  const navigate = useNavigate();
  const canUpdateStudents = adminHasPermission('update-students');
  const canDeleteStudents = adminHasPermission('delete-students');
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, student: null });
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, student: null });

  useEffect(() => {
    fetchStudents();
  }, [page, rowsPerPage, searchTerm]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('admin/students-with-courses', {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchTerm
        }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setStudents(data.data || []);
        setTotalRecords(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch student list');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (student) => {
    setDeleteDialog({ open: true, student });
  };

  const handleDeleteConfirm = async () => {
    const { student } = deleteDialog;
    try {
      const response = await api.delete(`admin/student/${student.id}`);
      if (response.data.success) {
        toast.success('Student deleted successfully');
        fetchStudents(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      if (error.response?.status === 400) {
        toast.error('Cannot delete student because they are assigned to courses');
      } else {
        toast.error('Failed to delete student');
      }
    } finally {
      setDeleteDialog({ open: false, student: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, student: null });
  };

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: 0,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)'
        }}>
          <SchoolIcon sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            Student Management
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          Manage student accounts, view assigned courses, and handle student data
        </Typography>
      </Box>

      <Card sx={{ 
        borderRadius: 4, 
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)'
      }}>
        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: 'calc(100dvh - 200px)', overflow: 'hidden' }}>
          <Box sx={{ mb: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search students..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                width: 350,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ResponsiveTable
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.05)',
              flex: 1,
              minHeight: 0,
              scrollbarGutter: 'stable',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Parent Details</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>School year</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Academic year</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Courses</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Past papers</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Papers</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Mock exams</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 2, background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">Loading students...</Typography>
                    </TableCell>
                  </TableRow>
                ) : students.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No students found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student) => (
                    <TableRow 
                      key={student.id} 
                      hover
                      sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                            width: 40, 
                            height: 40,
                            fontSize: '1rem',
                            fontWeight: 600
                          }}>
                            {student.student?.first_name?.[0] || 'S'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} color="#2d3748">
                              {student.student?.first_name} {student.student?.last_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {student.student?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {student.parent ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600} color="#2d3748">
                              {student.parent.full_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {student.parent.email}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No parent assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} sx={{ color: '#4a5568' }}>
                          {student.display_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.school_year?.name || '—'}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(102, 126, 234, 0.1)',
                            color: '#667eea',
                            fontWeight: 600,
                            borderRadius: '6px'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                          {student.academic_year || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.assigned_courses_count || 0}
                          size="small"
                          color={(student.assigned_courses_count || 0) > 0 ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ fontWeight: 600, borderRadius: '6px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.assigned_past_papers_count ?? 0}
                          size="small"
                          color={(student.assigned_past_papers_count || 0) > 0 ? 'secondary' : 'default'}
                          variant="outlined"
                          sx={{ fontWeight: 600, borderRadius: '6px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.assigned_papers_count ?? 0}
                          size="small"
                          color={(student.assigned_papers_count || 0) > 0 ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ fontWeight: 600, borderRadius: '6px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.assigned_mock_exams_count ?? 0}
                          size="small"
                          color={(student.assigned_mock_exams_count || 0) > 0 ? 'info' : 'default'}
                          variant="outlined"
                          sx={{ fontWeight: 600, borderRadius: '6px' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/admin/student/${student.id}`)}
                              sx={{ 
                                color: '#667eea',
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {canUpdateStudents && (
                          <Tooltip title="Purchase course / mock / paper" arrow>
                            <IconButton
                              size="small"
                              onClick={() => setPurchaseDialog({ open: true, student })}
                              sx={{
                                color: '#0d9488',
                                bgcolor: 'rgba(13, 148, 136, 0.1)',
                                '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.2)' }
                              }}
                            >
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          )}
                          {canUpdateStudents && (
                          <Tooltip title="Edit Student" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/admin/student/${student.id}/edit`)}
                              sx={{ 
                                color: '#f59e0b',
                                bgcolor: 'rgba(245, 158, 11, 0.1)',
                                '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          )}
                          
                          {canDeleteStudents && (
                          <Tooltip title="Delete Student" arrow>
                            <IconButton 
                              size="small"
                              onClick={() => handleDeleteClick(student)}
                              sx={{ 
                                color: '#ef4444',
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}
          />
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <StudentPurchaseResourcesDialog
        open={purchaseDialog.open}
        student={purchaseDialog.student}
        onClose={(didPurchase) => {
          setPurchaseDialog({ open: false, student: null });
          if (didPurchase) fetchStudents();
        }}
      />

      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the student "{deleteDialog.student?.display_name || 'Unknown'}"? 
            This action cannot be undone.
          </Typography>
          {deleteDialog.student?.assigned_courses_count > 0 && (
            <Typography color="warning.main" sx={{ mt: 2 }}>
              Warning: This student has {deleteDialog.student.assigned_courses_count} assigned courses. 
              Deletion may be blocked if courses are still active.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentList;
